import THDStorageUtils, { LOCAL_STORAGE } from '@thd-olt-global/thd-storage-utils';
import { FALLBACK_SESSION_ID, MAA_SESSION_KEY } from '../components/MagicApronAssistant/constants';
import { getUserName } from './window-utils';
import { logger } from './logger';

/**
 * Returns a string to be used as session ID.
 *
 * @return { string }
 */
export const getSessionId = () => {
  return typeof window !== 'undefined'
    ? window?.cookieUtils?.readBrowserCookie('thda.s') || FALLBACK_SESSION_ID : FALLBACK_SESSION_ID;
};

export const getDefaultSession = () => ({
  sessionId: getSessionId(),
  timestamp: Date.now(),
  shouldAutoOpen: false,
});

/**
 * Reads the session the assistant is actually running on, without seeding one. `useLocalStorage`
 * is deliberately avoided here: passing it a default writes that default to storage when the key
 * is absent, so an entry point that renders before the assistant would invent the session the
 * assistant then adopts. Reading is not this module's job to make persistent.
 *
 * @return { Session | undefined }
 */
export const readSession = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    return THDStorageUtils.get(LOCAL_STORAGE, { key: MAA_SESSION_KEY, returnString: false });
  } catch (error) {
    logger.error({
      message: `Error occurred while accessing local storage - ${MAA_SESSION_KEY}`,
      error
    });
    return undefined;
  }
};

/**
 * The session id to report a click under. Prefers the assistant's live session so a consumer's
 * analytics join up with the assistant's own, and falls back to a fresh cookie read on a page
 * where the assistant has never run.
 *
 * @return { string }
 */
export const getActiveSessionId = () => readSession()?.sessionId || getSessionId();

export const createActionId = (sessionId) => {
  if (!sessionId || sessionId === FALLBACK_SESSION_ID) {
    return `${crypto.randomUUID()}_${Date.now().toString()}`;
  }
  return `${sessionId}_${Date.now().toString()}`;
};

/**
 * @typedef { Object } Session
 * @property { string } sessionId - User ID string used as sessionId.
 * @property { number } timestamp - Timestamp when session was created.
 */

export const getInitialMessage = (sessionId) => {
  const authUser = getUserName();
  return {
    type: 'bot',
    content: {
      responseSummary: {
        // eslint-disable-next-line max-len
        summaryAnswer: 'What can I help you with today?',
        relatedQuestions: {
          suggestedQuestions: [],
        }
      },
    },
    sessionId,
    // Set to true since no feedback prompt should be shown for initial message
    feedbackProvided: true,
    timestamp: Date.now(),
    isInitialMessage: true,
  };
};
